import { UUID } from "@bitory-gmbh/nidavellir";
import {
  PatchRequest,
  updateHtzSummaryBaseDataCache,
  useHtzSummaryBaseDataPatchMutation,
  useHtzSummaryBaseDataShowQuery,
} from "@/services/backend/htz/summary/api/base-data-api";
import { CardContentSkeleton } from "@/shared/components/card-skeleton";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import t from "@/lang/lang";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";
import { inputValidationErrorMessages } from "@/shared/components/errors/input-error-messages";
import { TextInputField } from "@/shared/components/field/input-field";
import { EditorInputField } from "@/shared/components/field/editor-input-field";

export function SummaryBaseDataFields({ id }: { id: UUID }) {
  const {
    data,
    error: dataError,
    isLoading: dataLoading,
  } = useHtzSummaryBaseDataShowQuery({ id });

  const request: PatchRequest = { id };
  const [set, { error }] = useOptimisticDebouncedMutation(
    request,
    updateHtzSummaryBaseDataCache,
    useHtzSummaryBaseDataPatchMutation(),
  );

  if (dataError) {
    return <RTKQueryErrorAlert error={dataError} />;
  }

  if (dataLoading || !data) {
    return <CardContentSkeleton />;
  }

  return (
    <div className="grid gap-4">
      {/* The label is unique per company, so a rename can be rejected
          with a conflict rather than a field error. */}
      <RTKQueryErrorAlert error={error} />
      <TextInputField
        label={t("Bezeichnung")}
        description={t(
          "Unterscheidet die Basisdaten eines Unternehmens voneinander.",
        )}
        text={data.label}
        onTextChange={(label) => set({ ...request, label }, { ...data, label })}
        errors={inputValidationErrorMessages(error, "label")}
      />
      <TextInputField
        label={t("Titel")}
        text={data.title}
        onTextChange={(title) => set({ ...request, title }, { ...data, title })}
        errors={inputValidationErrorMessages(error, "title")}
      />
      <TextInputField
        label={t("Untertitel")}
        text={data.subtitle}
        onTextChange={(subtitle) =>
          set({ ...request, subtitle }, { ...data, subtitle })
        }
        errors={inputValidationErrorMessages(error, "subtitle")}
      />
      <EditorInputField
        label={t("Anschreiben")}
        description={t(
          "Text, mit dem ein neuer Inspektionsbericht den Kunden anspricht.",
        )}
        initialHtml={data.coverLetter}
        onHtmlChange={(coverLetter: string) =>
          set({ ...request, coverLetter }, { ...data, coverLetter })
        }
        errors={inputValidationErrorMessages(error, "coverLetter")}
      />
    </div>
  );
}
